import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HomeHeader, { type SitePath } from "./HomeHeader";
import HomeFooter from "./HomeFooter";
import HomeFonts from "./HomeFonts";

/** The page names the new site's pages carry (data-home-page), for tests and the browser checks. */
export type SitePage = "home" | "studio" | "download" | "app" | "app-legal";

/**
 * The frame every page of the new site shares (home, /studio, /download, /personal, /personal/*): the fonts, a skip link,
 * the header and the footer, on the dashboard's light ground (the tokens: src/pages/home/ui.ts). In-page anchors (#waitlist, #pricing, #features) are scrolled to on arrival and on
 * a hash change; #waitlist also focuses the email field.
 */
const SiteFrame = ({ page, path, children, mainClassName = "" }: { page: SitePage; path: SitePath; children: ReactNode; mainClassName?: string }) => {
  const { t } = useTranslation();
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!el) return;
    el.scrollIntoView({ block: "start" });
    if (hash === "#waitlist") el.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
  }, [pathname, hash]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-background font-sans text-foreground" data-home-page={page}>
      <HomeFonts />
      <a
        href="#home-main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-background focus:px-4 focus:py-2 focus:text-foreground focus:shadow-md"
      >
        {t("home.skip")}
      </a>
      <HomeHeader path={path} />
      <main id="home-main" tabIndex={-1} className={`outline-none ${mainClassName}`}>
        {children}
      </main>
      <HomeFooter />
    </div>
  );
};

export default SiteFrame;
